import { useMemo } from "react";
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { SongbookUb } from "../gql/graphql";
import { useDeleteSongbookMutation } from "./useDeleteSongbookMutation";
import { useHotkeys } from "react-hotkeys-hook";
import { useSongnumbersBySongbookQuery } from "../items/item/useSongnumbersBySongbookQuery";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  toggle: () => void;
  bucket: string;
  songbook?: SongbookUb;
  onDeleted?: () => void;
}

export function DeleteSongbookDialog({
  isOpen,
  toggle,
  bucket,
  songbook,
  onDeleted,
}: Props) {
  const { t } = useTranslation();
  const [, deleteSongbook] = useDeleteSongbookMutation();
  const { songnumbers, fetching, errorMessage } = useSongnumbersBySongbookQuery(
    bucket,
    isOpen ? (songbook?.id ?? null) : null,
  );

  const assignedSongnumberCount = songnumbers.length;
  const hasAssignedSongnumbers = !fetching && assignedSongnumberCount > 0;
  const isValid =
    songbook !== undefined &&
    !fetching &&
    !errorMessage &&
    !hasAssignedSongnumbers;

  const assignmentMessage = useMemo(() => {
    if (assignedSongnumberCount === 1) {
      return t("songbooks.deleteDialog.oneAssignmentLeft");
    }

    return t("songbooks.deleteDialog.multipleAssignmentsLeft", {
      count: assignedSongnumberCount,
    });
  }, [assignedSongnumberCount, t]);

  const handleDelete = () => {
    if (!isValid || !songbook) return;
    deleteSongbook({ id: songbook.id, bucket }).then((result) => {
      if (!result.error) {
        onDeleted?.();
        toggle();
      }
    });
  };

  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (isValid) {
        handleDelete();
      }
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      toggle();
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  if (!songbook) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {t("songbooks.deleteDialog.title")}
      </ModalHeader>
      <ModalBody>
        {fetching ? (
          <Alert color="info" fade={false}>
            {t("songbooks.deleteDialog.loadingCheckAssignments", {
              songbookName: songbook.name,
              mnemonic: songbook.mnemonic,
            })}
          </Alert>
        ) : errorMessage ? (
          <Alert color="danger" fade={false}>
            <h4 className="alert-heading">
              {t("songbooks.deleteDialog.errorCheckingAssignments")}
            </h4>
            <p>{t("songbooks.deleteDialog.errorCheckingAssignmentsDesc")}</p>
            <hr />
            <p className="mb-0">{t("songbooks.deleteDialog.errorTryAgain")}</p>
          </Alert>
        ) : hasAssignedSongnumbers ? (
          <Alert color="danger" fade={false}>
            <h4 className="alert-heading">
              {t("songbooks.deleteDialog.cannotDeleteWithAssignments")}
            </h4>
            <p>{assignmentMessage}</p>
            <hr />
            <p className="mb-0">
              {t("songbooks.deleteDialog.removeAssignmentsFirst")}
            </p>
          </Alert>
        ) : (
          <>
            {t("songbooks.deleteDialog.noAssignments", {
              songbookName: songbook.name,
              mnemonic: songbook.mnemonic,
            })}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          {t("common.buttons.cancel")}
        </Button>
        <Button color="danger" onClick={handleDelete} disabled={!isValid}>
          {t("songbooks.deleteDialog.deleteButton")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
